import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, errMsg } from '../pages/organization/finance/shared';
import Icon from './dashboard/DashboardIcons';

const STEPS = ['بيانات المنشأة', 'النشاط والفئات', 'العملة والضريبة', 'أول لاعب'];
const SPORT_TYPES = ['كرة القدم', 'كرة السلة', 'كرة الطائرة', 'كرة اليد', 'أخرى'];

export default function OnboardingWizard({ open, settings, onClose }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({});
  const [categories, setCategories] = useState([]);
  const [catForm, setCatForm] = useState({ name: '', min_age: '', max_age: '' });
  const [seasonForm, setSeasonForm] = useState({ name: '', start_date: '', end_date: '' });
  const [seasonCreated, setSeasonCreated] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setForm({
        club_name: settings.club_name || '',
        phone: settings.phone || settings.club_phone || '',
        email: settings.email || settings.club_email || '',
        location: settings.club_city || settings.location || '',
        sport_type: settings.sport_type || '',
        vat_registered: settings.vat_registered || false,
        vat_number: settings.vat_number || '',
        vat_rate: settings.vat_rate ?? 15,
        logo_file_id: settings.logo_file_id || null,
      });
      setStep(1);
      setErr(null);
      setSeasonCreated(false);
    }
  }, [open, settings]);

  if (!open) return null;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/files/upload`, fd, auth());
      const fileId = res.data?.file_id || res.data?.id;
      await axios.put(`${API}/club-settings`, { club_name: form.club_name || settings?.club_name || 'المنشأة', logo_file_id: fileId }, auth());
      setForm((f) => ({ ...f, logo_file_id: fileId }));
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploadingLogo(false);
  };

  const createSeason = async () => {
    if (!seasonForm.name.trim() || !seasonForm.start_date || !seasonForm.end_date) return;
    try {
      const res = await axios.post(`${API}/seasons`, seasonForm, auth());
      await axios.post(`${API}/seasons/${res.data.id}/activate`, {}, auth());
      setSeasonCreated(true);
    } catch (e) { setErr(errMsg(e, 'فشل إنشاء الموسم')); }
  };

  const save = async (extra = {}) => {
    setSaving(true); setErr(null);
    try {
      await axios.put(`${API}/club-settings`, {
        club_name: form.club_name || settings?.club_name || 'المنشأة',
        phone: form.phone || null,
        email: form.email || null,
        location: form.location || null,
        sport_type: form.sport_type || null,
        vat_registered: form.vat_registered,
        vat_number: form.vat_registered ? (form.vat_number || null) : null,
        vat_rate: Number(form.vat_rate) || 15,
        ...extra,
      }, auth());
      return true;
    } catch (e) { setErr(errMsg(e, 'فشل الحفظ')); return false; }
    finally { setSaving(false); }
  };

  const next = async () => {
    if (step === 1 && !form.club_name?.trim()) { setErr('اسم المنشأة مطلوب'); return; }
    if (step === 3 && form.vat_registered && !form.vat_number?.trim()) { setErr('الرقم الضريبي مطلوب عند التسجيل بالضريبة'); return; }
    const ok = await save();
    if (!ok) return;
    if (step === 2 && !seasonCreated) await createSeason();
    if (step < 4) setStep(step + 1);
  };
  const skipForNow = async () => { await save({ onboarding_completed: true }); onClose(true); };
  const finish = async () => { const ok = await save({ onboarding_completed: true }); if (ok) onClose(true); };

  const addCategory = async () => {
    if (!catForm.name.trim()) return;
    setErr(null);
    try {
      const res = await axios.post(`${API}/categories`, {
        name: catForm.name.trim(),
        min_age: catForm.min_age ? Number(catForm.min_age) : null,
        max_age: catForm.max_age ? Number(catForm.max_age) : null,
      }, auth());
      setCategories((c) => [...c, res.data]);
      setCatForm({ name: '', min_age: '', max_age: '' });
    } catch (e) { setErr(errMsg(e, 'فشل إضافة الفئة')); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4" data-testid="onboarding-wizard-overlay">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="onboarding-wizard">
        <div className="border-b border-slate-100 px-6 pt-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-lg font-bold text-slate-900">مرحباً بك في تسيار 👋</div>
              <div className="text-xs text-slate-500">إعداد سريع لمنشأتك — {STEPS[step - 1]} ({step}/4)</div>
            </div>
            <button onClick={skipForNow} className="text-xs font-semibold text-slate-400 hover:text-slate-600" data-testid="onboarding-skip-btn">الإعداد لاحقًا</button>
          </div>
          <div className="mt-3 mb-4 flex gap-1.5" data-testid="onboarding-progress-bar">
            {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? 'bg-[#16233b]' : 'bg-slate-100'}`} />)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="onboarding-error">{err}</div>}

          {step === 1 && (
            <div className="space-y-3" data-testid="onboarding-step-1">
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                    {form.logo_file_id ? <img src={`${API}/files/${form.logo_file_id}`} alt="الشعار" className="h-full w-full object-contain" /> : <Icon name="building" className="h-7 w-7 text-slate-300" />}
                  </div>
                  <label className="absolute -bottom-1 -left-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-teal-600 text-white shadow-md hover:bg-teal-700" title="رفع الشعار" data-testid="onboarding-logo-change-btn">
                    <Icon name="upload" className="h-3 w-3" />
                    <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="onboarding-logo-input" />
                  </label>
                  {uploadingLogo && <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70 text-[9px] font-bold text-teal-700">جارٍ الرفع...</div>}
                </div>
                <div className="text-xs text-slate-500">شعار المنشأة — يظهر في الفواتير والتقارير والبطاقات. يمكنك رفعه الآن أو تأجيله لصفحة الإعدادات.</div>
              </div>
              <Field label="اسم المنشأة" required><input className={inputCls} value={form.club_name || ''} onChange={(e) => setForm({ ...form, club_name: e.target.value })} data-testid="onboarding-club-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="الهاتف"><input dir="ltr" className={inputCls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="onboarding-phone" /></Field>
                <Field label="البريد الإلكتروني"><input dir="ltr" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="onboarding-email" /></Field>
              </div>
              <Field label="المدينة"><input className={inputCls} value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="onboarding-location" /></Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-2">
              <Field label="النشاط الرياضي الأساسي">
                <select className={inputCls} value={form.sport_type || ''} onChange={(e) => setForm({ ...form, sport_type: e.target.value })} data-testid="onboarding-sport-type">
                  <option value="">اختر النشاط...</option>
                  {SPORT_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <div className="mb-2 text-sm font-bold text-slate-700">أضف فئة عمرية (اختياري)</div>
                <div className="grid grid-cols-4 gap-2">
                  <input placeholder="اسم الفئة" className={`${inputCls} col-span-2`} value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="onboarding-cat-name" />
                  <input placeholder="من عمر" type="number" className={inputCls} value={catForm.min_age} onChange={(e) => setCatForm({ ...catForm, min_age: e.target.value })} data-testid="onboarding-cat-min" />
                  <input placeholder="إلى عمر" type="number" className={inputCls} value={catForm.max_age} onChange={(e) => setCatForm({ ...catForm, max_age: e.target.value })} data-testid="onboarding-cat-max" />
                </div>
                <button type="button" onClick={addCategory} className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-teal-300" data-testid="onboarding-cat-add-btn">+ إضافة فئة</button>
                {categories.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="onboarding-cat-list">
                    {categories.map((c) => <span key={c.id} className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">{c.name}{c.min_age != null || c.max_age != null ? ` (${c.min_age ?? ''}-${c.max_age ?? ''})` : ''}</span>)}
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <div className="mb-2 text-sm font-bold text-slate-700">الموسم الحالي (اختياري)</div>
                <div className="text-xs text-slate-500 mb-2">يحدد نطاق بيانات اللاعبين والمباريات، ويمكنك إنشاء مواسم أخرى لاحقاً من أيقونة التقويم بأعلى الصفحة.</div>
                {seasonCreated ? (
                  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700" data-testid="onboarding-season-created">
                    <Icon name="check" className="h-4 w-4" /> تم إنشاء الموسم وتفعيله
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <input placeholder="مثال: 2025-2026" className={`${inputCls} col-span-1`} value={seasonForm.name} onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })} data-testid="onboarding-season-name" />
                    <input type="date" className={inputCls} value={seasonForm.start_date} onChange={(e) => setSeasonForm({ ...seasonForm, start_date: e.target.value })} data-testid="onboarding-season-start" />
                    <input type="date" className={inputCls} value={seasonForm.end_date} onChange={(e) => setSeasonForm({ ...seasonForm, end_date: e.target.value })} data-testid="onboarding-season-end" />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-3">
              <Field label="العملة"><input className={`${inputCls} bg-slate-50 text-slate-500`} value="ريال سعودي (SAR)" disabled /></Field>
              <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <label className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-slate-800">المنشأة مسجلة في ضريبة القيمة المضافة</div>
                    <div className="text-xs text-slate-500">يمكنك تفعيلها لاحقاً من الإعدادات في أي وقت</div>
                  </div>
                  <input type="checkbox" checked={form.vat_registered || false} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} className="h-5 w-5" data-testid="onboarding-vat-toggle" />
                </label>
                {form.vat_registered && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Field label="الرقم الضريبي" required><input className={inputCls} value={form.vat_number || ''} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="onboarding-vat-number" /></Field>
                    <Field label="نسبة الضريبة %"><input type="number" className={inputCls} value={form.vat_rate ?? 15} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="onboarding-vat-rate" /></Field>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="onboarding-step-4">
              <p className="text-sm text-slate-600">أضف أول لاعب لديك الآن أو استورد قائمة كاملة من ملف Excel — يمكنك دائماً القيام بذلك لاحقاً من صفحة اللاعبين.</p>
              <div className="grid grid-cols-2 gap-3">
                <a href="/players" onClick={finish} className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 p-5 text-center hover:border-teal-300" data-testid="onboarding-goto-add-player">
                  <Icon name="users" className="w-6 h-6 text-[#16233b]" />
                  <span className="text-sm font-semibold text-slate-800">إضافة لاعب</span>
                </a>
                <a href="/players" onClick={finish} className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 p-5 text-center hover:border-teal-300" data-testid="onboarding-goto-import">
                  <Icon name="arrowUp" className="w-6 h-6 text-[#16233b]" />
                  <span className="text-sm font-semibold text-slate-800">استيراد من Excel</span>
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 disabled:opacity-0" data-testid="onboarding-prev-btn">السابق</button>
          {step < 4
            ? <button onClick={next} disabled={saving} className="rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="onboarding-next-btn">{saving ? 'جارٍ الحفظ...' : 'التالي'}</button>
            : <button onClick={finish} disabled={saving} className="rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="onboarding-finish-btn">إنهاء الإعداد</button>}
        </div>
      </div>
    </div>
  );
}
